import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  DateFormat,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { ApiCommand } from '~/lib/Api';
import {
  CalendarResource,
  CalendarResourceMapper,
  FETCH_APPOINTMENT_TYPES_ERROR,
  WEEKLY_AVAILABILITY_LABEL,
} from '~/lib/constants/calendar.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi } from '~/lib/hooks';
import useCalendarApi from '~/lib/hooks/useCalendarApi';
import useCalendarElement from '~/lib/hooks/useCalendarElement';
import {
  DEFAULT_AVAILABILITY_SLOT_INTERVAL,
  formatTimeLabels,
  getManageAvailabilitySelectProviderHelperText,
  getPrimaryProviderTypeForAppointmentType,
  getProviderUniversalSelectOption,
} from '~/lib/util/calendar.util';
import { getPreferredFullName } from '~/lib/util/patient.util';

import ActionBar from './ActionBar';
import CalendarError from './CalendarError';
import TimezoneSelect from './TimezoneSelect';

import './availability-rules.css';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';
import RulesSelectV2 from '~/components/common/calendar/RulesSelectV2';
import { useGetAllOutOfOfficeListQuery } from '~/store/services/profile';

const TOP_LEVEL_ID = 'availability-rules';

const localTzid = DateTime.now().zoneName;
interface OnResetCalendarAccountParams {
  provider: SelectedProvider | SelectedProviderFromUniversalSelect;
}

interface OnGenerateElementTokenParams extends OnResetCalendarAccountParams {
  elementToken: string;
}

interface HandleRuleSelectParams {
  allAppointmentTypes?: AppointmentType[];
  calendarId?: string;
  elementToken?: string;
  existingRuleExternalId?: string;
  newRuleExternalId: string;
  userId?: string;
}

const AvailabilityRules = () => {
  const { calendarAccount, currentUser, permissions } = useGlobalContext();

  const { data: outOfOfficeListData } = useGetAllOutOfOfficeListQuery({
    providerExternalId: currentUser?.userId,
    startDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
  });
  const { calendarId: myCalendarId } = calendarAccount ?? {};
  const hasAvailabilityPermission =
    permissions[MaudPermissions.MAUD_AVAILABILITY];
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const [currentRule, setCurrentRule] = useState<string>('');
  // Storing new tz as a ref so that the latest value is available to the save availability callback
  const newTzidRef = useRef<string>(null);
  const [calendarAccountNotFound, setCalendarAccountNotFound] =
    useState<string>('');
  const [hasInitializedCurrentUserState, setHasInitializedCurrentUserState] =
    useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] =
    useState<SelectedProvider>(null);
  const [selectedProviderValue, setSelectedProviderValue] =
    useState<SelectedProviderFromUniversalSelect>(null);

  const selectedProviderUniversalSelectOption =
    useMemo<ProviderUniversalSelectOption>(
      () => getProviderUniversalSelectOption(selectedProviderValue),
      [selectedProviderValue],
    );

  const {
    elementToken: element_token,
    generateElementToken,
    refreshElement,
    setOptions,
    updateElement,
  } = useCalendarElement(CalendarResource.AVAILABILITY_RULES, formatTimeLabels);

  const {
    data: appointmentTypesData,
    error: getAppointmentTypesError,
    loading: isAppointmentTypesLoading,
    sendRequest: getAppointmentTypes,
  } = useApi<AppointmentType[]>();

  const { sendRequest: getAccounts } = useApi<CalendarAccount[]>();

  const selectedAppointmentType = useMemo<AppointmentType>(
    () =>
      appointmentTypesData?.find(
        ({ appointmentTypeExtId }: AppointmentType) =>
          appointmentTypeExtId === currentRule,
      ),
    [appointmentTypesData, currentRule],
  );

  const {
    getAvailabilityRule,
    isSaveAvailabilityRuleError,
    saveAvailabilityRule,
    updateAvailabilityRuleRequestParams,
  } = useCalendarApi();

  const { saveAvailability, setComponentVisibility } =
    useComponentPortalContext();
  const { hasUnsavedChanges } = saveAvailability;

  const setHasUnsavedChanges = (value: boolean): void => {
    setComponentVisibility({
      component: PortalComponent.SAVE_AVAILABILITY,
      hasUnsavedChanges: value,
    });
  };

  /**
   * Storing save functionality in a React ref and updating the ref
   * with a side effect allows the function to be called from the Cronofy
   * callback function with access to the state variables in this component
   * as well as in "useCalendarApi"
   */
  const handleSaveRef =
    useRef<(params?: AvailabilityRule) => Promise<void>>(null);

  useEffect(() => {
    handleSaveRef.current = async (
      params?: AvailabilityRule,
    ): Promise<void> => {
      const statusCode = await saveAvailabilityRule(element_token, params);
      if (statusCode === 200) {
        setHasUnsavedChanges(false);
        refreshElement();
      }
    };
  }, [element_token, refreshElement]);

  const calendarEventCallback =
    useRef<(data: CalendarEvent) => Promise<void>>(null);

  const setCalendarEventCallback = useCallback(
    (availabilityRuleId: string, calendarId: string) =>
      async (data: CalendarEvent): Promise<void> => {
        const { type } = data?.notification ?? {};
        if (type === 'availability_rule_not_found') {
          const params = {
            availability_rule_id: availabilityRuleId,
            calendar_ids: [calendarId],
            tzid: localTzid,
            weekly_periods: [], // New availability rule should include no time slots by default
          };
          newTzidRef.current = localTzid;
          updateAvailabilityRuleRequestParams(params);
          // Auto-save new availability rule using default params
          await handleSaveRef?.current(params);
        }
        if (type === 'availability_rule_edited') {
          updateAvailabilityRuleRequestParams({
            weekly_periods: data.availability_rule.weekly_periods,
          });
          setHasUnsavedChanges(true);
        }
      },
    [],
  );

  const handleRuleSelect = useCallback(
    async ({
      allAppointmentTypes = appointmentTypesData,
      calendarId = selectedProviderValue?.calendarId,
      elementToken = element_token,
      existingRuleExternalId = selectedAppointmentType?.appointmentTypeExtId,
      newRuleExternalId,
      userId,
    }: HandleRuleSelectParams): Promise<void> => {
      if (newRuleExternalId !== existingRuleExternalId) {
        const {
          cronofyRule,
          appointmentType,
          appointmentTypeExtId,
          endTime: end_time,
          startTime: start_time,
        } = (allAppointmentTypes ?? appointmentTypesData).find(
          ({ appointmentTypeExtId }: AppointmentType) =>
            appointmentTypeExtId === newRuleExternalId,
        ) ?? {};

        calendarEventCallback.current = setCalendarEventCallback(
          cronofyRule ?? appointmentType,
          calendarId,
        );

        const existing = await getAvailabilityRule(
          cronofyRule ?? appointmentType,
          elementToken,
        );

        if (existing) {
          updateAvailabilityRuleRequestParams(existing);
          newTzidRef.current = existing.tzid;
        }

        setCurrentRule(appointmentTypeExtId);

        const newOptions = {
          ...CalendarResourceMapper[CalendarResource.AVAILABILITY_RULES]
            .options,
          availability_rule_id: cronofyRule ?? appointmentType,
          callback: calendarEventCallback.current,
          config: {
            /**
             * Specifying this default availability on Sundays at 12:00am - 12:30am simply to avoid temporarily
             * displaying time slots immediately after a new availability rule loads and before the rule is auto-saved
             */
            default_weekly_periods: [
              { day: 'sunday', end_time: '00:30', start_time: '00:00' },
            ],
            duration: DEFAULT_AVAILABILITY_SLOT_INTERVAL,
            end_time,
            start_time,
          },
          element_token: elementToken,
          tzid: existing?.tzid ?? localTzid,
        };

        // "userId" is null when initializing the Cronofy element for the current user, in which case
        // calling setOptions() enables immediately toggling between different rules for the same user
        if (!userId) setOptions(newOptions);
        // manually update the Cronofy element when a new provider is selected ("userId" is defined)
        else updateElement(newOptions);
      }
    },
    [
      appointmentTypesData,
      element_token,
      selectedAppointmentType?.appointmentTypeExtId,
      selectedProvider?.calendarId,
      selectedProviderValue?.calendarId,
    ],
  );

  const handleTimezoneSelect = (timezoneId: string): void => {
    newTzidRef.current = timezoneId;
    updateAvailabilityRuleRequestParams({ tzid: timezoneId });
    setHasUnsavedChanges(true);
  };

  const fetchAppointmentTypes = (
    providerTypeName: string,
    calendarId: string,
    elementToken: string,
    userId: string,
  ): void => {
    getAppointmentTypes({
      callback: (data: AppointmentType[]) => {
        if (data?.length) {
          handleRuleSelect({
            allAppointmentTypes: data,
            calendarId,
            elementToken,
            newRuleExternalId: data[0].appointmentTypeExtId,
            userId,
          });
        }
      },
      command: ApiCommand.GET,
      url: urlConstants.calendar.getAppointmentTypes(
        getPrimaryProviderTypeForAppointmentType(providerTypeName),
      ),
    });
  };

  const onGenerateElementToken = useCallback(
    ({ provider, elementToken }: OnGenerateElementTokenParams) => {
      if (elementToken) {
        const { calendarId, providerTypeName, userId } = provider;
        setSelectedProvider(provider);
        setSelectedProviderValue(
          provider as SelectedProviderFromUniversalSelect,
        );
        fetchAppointmentTypes(
          providerTypeName,
          calendarId,
          elementToken,
          userId,
        );
      }
    },
    [],
  );
  /** Update selected provider state, fetch matching appointment types and an element token */
  const onResetCalendarAccount = useCallback(
    (params: OnResetCalendarAccountParams): void => {
      setCurrentRule('');
      const isInitialLoad = !hasInitializedCurrentUserState;
      generateElementToken({
        callback: (data: ElementTokenData) => {
          onGenerateElementToken({
            ...params,
            elementToken: data?.elementToken?.token,
          });
        },
        providerExternalId:
          isInitialLoad && hasAvailabilityPermission
            ? null
            : params.provider.userId,
      });
    },
    [onGenerateElementToken, hasInitializedCurrentUserState],
  );

  /** Fetch appointment types and element token for the current user (only once) */
  useEffect(() => {
    if (
      currentUser &&
      hasAvailabilityPermission &&
      !hasInitializedCurrentUserState &&
      myCalendarId
    ) {
      setHasInitializedCurrentUserState(true);

      const provider = {
        ...currentUser,
        outOfOffice: outOfOfficeListData,
        calendarId: myCalendarId,
      };

      onResetCalendarAccount({ provider });
    }
  }, [
    currentUser,
    hasAvailabilityPermission,
    hasInitializedCurrentUserState,
    myCalendarId,
    onResetCalendarAccount,
  ]);

  const handleUniversalProviderSelect = (
    selected: ProviderUniversalSelectOption,
  ): void => {
    setCalendarAccountNotFound('');

    const { userId, firstName, lastName, providerTypeDetails } = selected;
    getAccounts({
      callback: (data: CalendarAccount[]) => {
        if (data?.length) {
          onResetCalendarAccount({
            provider: {
              ...selected,
              calendarId: data[0].calendarId,
              providerTypeName: providerTypeDetails?.name,
            },
          });
        } else {
          setCalendarAccountNotFound(
            `${getPreferredFullName(null, firstName, lastName)} (${providerTypeDetails?.name})`,
          );
        }
      },
      command: ApiCommand.GET,
      options: { 'provider-external-ids': userId },
      url: urlConstants.calendar.getAccounts,
    });
  };

  // Re-format time labels whenever a user clicks either of the expand buttons
  const onCalendarClick = (e: React.BaseSyntheticEvent): void => {
    setTimeout(() => {
      if (
        Array.from(e?.target?.classList ?? []).includes(
          'availability-rules__time-expander-button',
        )
      ) {
        formatTimeLabels();
      }
    }, 125);
  };

  return (
    <ChakraBox
      height="100%"
      overflow="auto"
      paddingLeft="4px"
      paddingRight="24px"
      paddingTop="26px"
    >
      {getAppointmentTypesError ? (
        <CalendarError
          id={`${TOP_LEVEL_ID}__get-appointment-types`}
          message={FETCH_APPOINTMENT_TYPES_ERROR}
        />
      ) : (
        <>
          <ChakraFlex flexDirection="column" gap="10px" marginBottom="24px">
            <Text
              color="neutral.primary"
              id={`${TOP_LEVEL_ID}__title`}
              variant="h5"
            >
              {WEEKLY_AVAILABILITY_LABEL}
            </Text>
            <Text>
              {getManageAvailabilitySelectProviderHelperText(isSchedulingAdmin)}
            </Text>
          </ChakraFlex>

          <ChakraSimpleGrid columns={1} spacing="8px" width="30%">
            <ProviderUniversalSelect
              handleSelect={handleUniversalProviderSelect}
              id={`${TOP_LEVEL_ID}__provider-universal-select`}
              isDisabled={!isSchedulingAdmin || hasUnsavedChanges}
              selectedProvider={selectedProviderUniversalSelectOption}
              shouldAddCalendarProfile
              shouldDisableProviderOptionsWithoutCalendarId
            />
          </ChakraSimpleGrid>
          {calendarAccountNotFound && (
            <ChakraBox paddingTop="18px">
              <CalendarError
                id={`${TOP_LEVEL_ID}__calendar-account-not-found`}
                message={`Calendar details not found for ${calendarAccountNotFound}`}
                position="center"
              />
            </ChakraBox>
          )}
          <ChakraBox
            alignItems={hasUnsavedChanges ? 'flex-start' : 'center'}
            display="flex"
            flexDirection={hasUnsavedChanges ? 'column-reverse' : 'row'}
            justifyContent="space-between"
            margin="24px 0px"
          >
            <RulesSelectV2
              appointmentTypes={appointmentTypesData}
              handleRuleSelect={(newRuleExternalId: string) =>
                handleRuleSelect({ newRuleExternalId })
              }
              isDisabled={hasUnsavedChanges}
              isLoading={isAppointmentTypesLoading}
              selectedAppointmentType={selectedAppointmentType}
              variant="availability"
            />

            {selectedAppointmentType && (
              <ChakraBox width="100%">
                <ActionBar
                  handleEditClick={() => setHasUnsavedChanges(true)}
                  handleSaveClick={handleSaveRef?.current}
                  hasUnsavedChanges={hasUnsavedChanges}
                  timezone={newTzidRef.current}
                />
                {isSaveAvailabilityRuleError && (
                  <ChakraBox paddingBottom="18px">
                    <CalendarError
                      id={`${TOP_LEVEL_ID}__save-availability`}
                      message="Error saving availability"
                      position="center"
                    />
                  </ChakraBox>
                )}
                {hasUnsavedChanges && (
                  <TimezoneSelect
                    handleSelect={handleTimezoneSelect}
                    value={newTzidRef.current}
                  />
                )}
              </ChakraBox>
            )}
          </ChakraBox>
          <ChakraBox
            display={currentRule === '' ? 'none' : undefined}
            id={
              CalendarResourceMapper[CalendarResource.AVAILABILITY_RULES]
                .options.target_id
            }
            onClick={onCalendarClick}
          />
        </>
      )}
    </ChakraBox>
  );
};

export default AvailabilityRules;
